import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { inr } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Check, X, CheckCircle2, Printer } from "lucide-react";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Public quote landing — accessed via token, no auth. */
export default function PublicQuote() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [adv, setAdv] = useState({ amount: 0, method: "upi", reference: "" });

  useEffect(() => {
    axios.get(`${API}/public/quotes/${token}`).then(r => {
      setQ(r.data);
      const due = Number(r.data.advance_amount_due || 0) || Math.round(Number(r.data.total || 0) * 0.5);
      setAdv(a => ({ ...a, amount: due }));
    }).catch(() => setErr("This quote link is invalid or has expired."));
  }, [token]);

  const act = async (path, body) => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/quotes/${token}/${path}`, body || {});
      if (path === "accept") {
        setQ({ ...q, status: "accepted" });
        setAdvanceOpen(true);
        toast.success("Quote accepted — please pay 50% advance to confirm production");
      } else {
        setQ({ ...q, status: "declined" });
        toast.success("Feedback received — thank you.");
      }
    } catch(e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const recordAdvance = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/quotes/${token}/record-advance`, adv);
      setQ({ ...q, status: "accepted", advance_paid: data.advance_paid });
      setAdvanceOpen(false);
      toast.success("Thank you! Your advance is recorded — we'll start production shortly.");
    } catch(e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  if (err) return <PublicShell><div className="text-center p-12"><div className="label-tiny mb-2 text-[#B83A3A]">Not available</div><p>{err}</p></div></PublicShell>;
  if (!q) return <PublicShell><div className="text-center p-12 text-[#757169]">Loading…</div></PublicShell>;

  const done = q.status === "accepted" || q.status === "declined";

  return (
    <PublicShell>
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="label-tiny mb-1">Quote</div>
          <h1 className="font-display text-3xl tracking-tight mono">{q.quote_no}</h1>
          <div className="text-sm text-[#757169] mt-1">Prepared for {q.customer_name}</div>
        </div>
        <button onClick={()=>window.print()} className="btn-ghost text-xs flex items-center gap-1 no-print"><Printer size={12}/> Print</button>
      </div>

      <div className="card-surface p-6 mb-6">
        <table className="data-table">
          <thead><tr><th>Item</th><th>Customization</th><th>Qty</th><th>Unit</th><th>Amount</th></tr></thead>
          <tbody>
            {q.items?.map(it => (
              <tr key={it.id}>
                <td className="font-medium">{it.product}</td>
                <td className="text-[#757169]">{it.customization || "—"}</td>
                <td className="mono">{it.qty}</td>
                <td className="mono">{inr(it.unit_price)}</td>
                <td className="mono">{inr(it.qty * it.unit_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-6 pt-4 border-t border-[#EAE5DB] flex justify-end">
          <div className="w-64 space-y-1.5 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span className="mono">{inr(q.subtotal)}</span></div>
            {(q.gst_breakup && q.gst_breakup.length > 0
              ? q.gst_breakup.map(b => (
                  <div key={b.slab} className="flex justify-between"><span>GST @ {b.slab}%</span><span className="mono">{inr(b.gst)}</span></div>
                ))
              : (
                <div className="flex justify-between"><span>GST ({q.gst_percent}%)</span><span className="mono">{inr(q.gst_amount)}</span></div>
              ))}
            <div className="flex justify-between font-display font-bold text-lg pt-2 border-t border-[#EAE5DB]"><span>Total</span><span className="mono">{inr(q.total)}</span></div>
          </div>
        </div>
      </div>

      {q.notes && <div className="text-sm text-[#757169] mb-6 whitespace-pre-line">{q.notes}</div>}

      {/* Payment details */}
      {q.payment_details && (
        <div className="card-surface p-6 mb-6" data-testid="payment-details">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
            <div className="label-tiny">Pay 50% advance to confirm production</div>
            <div className="font-display text-2xl font-bold text-[#C45A41]" data-testid="advance-amount">
              ₹{Number(q.advance_amount_due || Math.round(Number(q.total || 0) * 0.5)).toLocaleString("en-IN")}
              <span className="text-xs font-normal text-[#75706A] ml-2">of {inr(q.total)}</span>
            </div>
          </div>
          {q.advance_paid > 0 ? (
            <div className="p-3 rounded-md bg-[#DDEADE] border border-[#4A7258] mb-4 text-sm">
              <b className="text-[#2E4A38]">✓ Advance of {inr(q.advance_paid)} received</b>
              <div className="text-[#2E4A38] text-xs mt-0.5">Production will begin. Ref: {q.advance_ref || "—"}</div>
            </div>
          ) : null}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="text-sm space-y-1.5">
              {q.payment_details.bank_name && <div><span className="text-[#757169]">Bank:</span> <b>{q.payment_details.bank_name}</b></div>}
              {q.payment_details.account_holder && <div><span className="text-[#757169]">A/C name:</span> <b>{q.payment_details.account_holder}</b></div>}
              {q.payment_details.account_no && <div><span className="text-[#757169]">A/C no:</span> <span className="mono">{q.payment_details.account_no}</span></div>}
              {q.payment_details.ifsc && <div><span className="text-[#757169]">IFSC:</span> <span className="mono">{q.payment_details.ifsc}</span></div>}
              {q.payment_details.upi_id && <div className="pt-1"><span className="text-[#757169]">UPI:</span> <span className="mono">{q.payment_details.upi_id}</span></div>}
            </div>
            {q.payment_details.payment_qr_url && (
              <div className="flex justify-center md:justify-end">
                <img src={q.payment_details.payment_qr_url} alt="Payment QR" className="max-w-[180px] rounded" />
              </div>
            )}
          </div>
          {q.status !== "declined" && !q.advance_paid && (
            <div className="mt-4 pt-4 border-t border-[#EAE5DB] no-print">
              <button onClick={()=>setAdvanceOpen(true)} className="btn-primary text-sm" data-testid="record-advance-btn">
                I've paid the advance — record it
              </button>
              <span className="ml-2 text-xs text-[#75706A]">Enter UTR / txn reference once you've transferred the amount.</span>
            </div>
          )}
        </div>
      )}
      {q.terms && <div className="text-xs text-[#757169] mb-6 whitespace-pre-line border-t border-[#EAE5DB] pt-4">{q.terms}</div>}

      {done ? (
        <div className={`card-surface p-6 flex items-center gap-3 ${q.status === "accepted" ? "border-l-4 border-l-[#4A7258]" : "border-l-4 border-l-[#B83A3A]"}`}>
          {q.status === "accepted" ? <CheckCircle2 className="text-[#4A7258]" /> : <X className="text-[#B83A3A]" />}
          <div>
            <div className="font-display font-semibold">{q.status === "accepted" ? "Quote accepted" : "Quote declined"}</div>
            <div className="text-sm text-[#757169]">Thank you for your response. Our team will be in touch.</div>
          </div>
        </div>
      ) : (
        <div className="flex gap-3 no-print">
          <button onClick={()=>act("accept")} disabled={busy} className="btn-primary flex items-center gap-2" data-testid="public-accept"><Check size={16}/> Accept quote</button>
          <button onClick={()=>setDeclineOpen(true)} disabled={busy} className="btn-ghost flex items-center gap-2" data-testid="public-decline"><X size={16}/> Decline</button>
        </div>
      )}

      {declineOpen && !done && (
        <div className="card-surface p-5 mt-4 fade-up no-print">
          <div className="label-tiny mb-2">Care to tell us why? (optional)</div>
          <textarea rows={3} className="input-field mb-3" value={reason} onChange={e=>setReason(e.target.value)} placeholder="Too expensive / wrong scope / changed mind…" />
          <div className="flex gap-2 justify-end">
            <button className="btn-ghost" onClick={()=>setDeclineOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={()=>act("decline",{ reason })} data-testid="public-decline-confirm">Submit</button>
          </div>
        </div>
      )}

      {advanceOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 no-print" onClick={()=>setAdvanceOpen(false)}>
          <div className="card-surface p-6 max-w-md w-full" onClick={e=>e.stopPropagation()}>
            <div className="label-tiny mb-1">Record advance payment</div>
            <h3 className="font-display font-bold text-xl mb-3">Confirm your ₹{Number(adv.amount).toLocaleString("en-IN")} advance</h3>
            <p className="text-sm text-[#75706A] mb-4">Enter the UTR / txn reference from your bank/UPI transfer. Our team will verify and start production.</p>
            <div className="space-y-3">
              <div>
                <div className="label-tiny mb-1">Amount paid ₹</div>
                <input type="number" className="input-field" value={adv.amount} onChange={e=>setAdv({...adv, amount: Number(e.target.value)||0})} data-testid="advance-amount-input"/>
              </div>
              <div>
                <div className="label-tiny mb-1">Payment method</div>
                <select className="input-field" value={adv.method} onChange={e=>setAdv({...adv, method: e.target.value})} data-testid="advance-method">
                  <option value="upi">UPI</option>
                  <option value="bank">Bank transfer / NEFT / IMPS</option>
                  <option value="cash">Cash</option>
                </select>
              </div>
              <div>
                <div className="label-tiny mb-1">UTR / Reference #</div>
                <input className="input-field" placeholder="e.g. 42XY123..." value={adv.reference} onChange={e=>setAdv({...adv, reference: e.target.value})} data-testid="advance-reference"/>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button className="btn-ghost" onClick={()=>setAdvanceOpen(false)}>Cancel</button>
              <button disabled={busy || !adv.amount} className="btn-primary disabled:opacity-50" onClick={recordAdvance} data-testid="submit-advance">Submit</button>
            </div>
          </div>
        </div>
      )}
    </PublicShell>
  );
}

export function PublicShell({ children }) {
  return (
    <div className="min-h-screen" style={{ background: "#FDFBF7" }}>
      <style>{`@media print { .no-print{display:none!important} .card-surface{border:1px solid #ccc;box-shadow:none} }`}</style>
      <div className="max-w-3xl mx-auto p-6 md:p-10">
        <div className="mb-8"><Logo size={28} /></div>
        <div className="fade-up">{children}</div>
        <div className="mt-12 text-xs text-[#757169] text-center">
          Marsh Clothing · Custom apparel & corporate gifts
        </div>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
